import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useQuery } from '@tanstack/react-query';
import { File, Paths } from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import { useAtomValue } from 'jotai';
import { useRef, useState } from 'react';
import QRCode from 'react-native-qrcode-svg';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { primum_default_avatar } from '@/assets/images';
import { CopyField } from '@/components/CopyField';
import { ShareTargetSheet, type ShareTarget } from '@/components/ShareTargetSheet';
import { Header } from '@/components/header';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Button, ButtonText } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { ScrollView } from '@/components/ui/scroll-view';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ROUTES } from '@/constants/routes';
import { formatSmsNumber, SMS_OPT_IN_NOTICE } from '@/constants/smsOptIn';
import useClipboard from '@/hooks/useClipboard';
import { useColors } from '@/hooks/useColors';
import { useToaster } from '@/hooks/useToaster';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { userIdAtom } from '@/services/globalState';
import { getMyPublicLinkV2QueryOptions } from '@/services/myPublicLinkV2';
import {
  trackExpertLinkCopied,
  trackExpertLinkShared,
  type ExpertShareTarget,
} from '@/util/analyticsEvents';
import { getAvatarUrl } from '@/util/getAvatarUrl';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';
import { EXPERT_LINK_SHARE_TITLE, shareExpertLink, shareIntakeEmail } from '@/util/shareLink';

const SHARE_TITLE = EXPERT_LINK_SHARE_TITLE;

const QR_CODE_SIZE = 220;

/** The link without its scheme, so it reads like something a person would type. */
const displayUrl = (url: string) => url.replace(/^https?:\/\//, '');

const isShareCancellation = (error: unknown) =>
  error instanceof Error && (error.message === 'Share canceled' || error.name === 'AbortError');

/**
 * The signed-in expert's ways of being asked a question — a scannable code,
 * their ask link, and the address that turns an emailed question into an
 * inquiry — each copyable, and shareable through a chooser.
 *
 * The address is shown rather than explained because nothing else in the
 * product displays it; an expert who cannot see it has to derive it from the
 * slug in their link, which is a rule they should never have to learn.
 *
 * The text number is the expert's dedicated Primum number, shown only once one
 * has been assigned. It is always presented with the opt-in notice, and what
 * is copied carries the notice too, because the campaign registered for the
 * number promises that wherever it is shared the notice goes with it.
 *
 * The ways of being reached render independently. A link and an address can only be absent
 * together, but the link alone also goes missing when its web domain cannot be
 * resolved, and the address is still valid when it does — so one failing never
 * hides the other. The text number is assigned separately, by support, so it
 * can be present or absent whatever the others are, and the screen reports
 * nothing available only when all three are missing.
 *
 * The number can be copied but is not a share target: the share sheet carries
 * the link, the code and the address, and the number would need its own
 * notice-carrying payload there.
 */
export default function AccountQrCode() {
  const { colors } = useColors();

  const toast = useToaster();

  const { copyToClipboard } = useClipboard();

  const { bottom } = useSafeAreaInsets();

  const userId = useAtomValue(userIdAtom);

  const { data: userData } = useQuery(getCurrentUserV2QueryOptions(userId));

  const { data: link, isLoading, isError } = useQuery(getMyPublicLinkV2QueryOptions(userId));

  const qrCodeRef = useRef<{ toDataURL: (callback: (dataUrl: string) => void) => void }>(null);

  const [isShareSheetOpen, setIsShareSheetOpen] = useState(false);

  const url = link?.url ?? null;

  const intakeEmail = link?.intakeEmail ?? null;

  const smsNumber = link?.smsNumber ?? null;

  const { name } = getProfileNameAndInitials({
    firstName: userData?.firstName,
    lastName: userData?.lastName,
    identifiesAsDoctor: userData?.identifiesAsDoctor,
  });

  const avatarUrl = getAvatarUrl(userData?.avatar, true);

  const onCopied = (target: ExpertShareTarget) => {
    if (!userId) {
      return;
    }

    trackExpertLinkCopied({ expertId: Number(userId), target });
  };

  /**
   * The code leaves as a PNG, which only the native share sheet can carry, so
   * this target is offered on native alone.
   *
   * A device whose sharing service cannot take a file still gets the link,
   * which is what the code encodes: the reader asked to pass on a way of being
   * reached, and silently doing nothing is the one answer that is no use.
   */
  const shareQrCode = async (expertId: number, linkUrl: string) => {
    if (!qrCodeRef.current) {
      throw new Error('QR code not ready to share');
    }

    if (!(await Sharing.isAvailableAsync())) {
      await shareLink(expertId, linkUrl);

      return;
    }

    const dataUrl = await new Promise<string>((resolve) => {
      qrCodeRef.current?.toDataURL(resolve);
    });

    const file = new File(Paths.cache, 'primum-qr-code.png');

    file.write(dataUrl, { encoding: 'base64' });

    await Sharing.shareAsync(file.uri, { dialogTitle: SHARE_TITLE, mimeType: 'image/png' });

    trackExpertLinkShared({ expertId, channel: 'qr_image', target: 'qr_image' });
  };

  const shareLink = async (expertId: number, linkUrl: string) => {
    if (isWeb) {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ title: SHARE_TITLE, url: linkUrl });

        trackExpertLinkShared({ expertId, channel: 'web_share', target: 'link' });

        return;
      }

      await copyToClipboard(linkUrl, () => onCopied('link'), 'Your link copied');

      return;
    }

    if (await shareExpertLink(linkUrl)) {
      trackExpertLinkShared({ expertId, channel: 'native_share', target: 'link' });
    }
  };

  const shareEmail = async (expertId: number, address: string) => {
    if (isWeb) {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ title: SHARE_TITLE, text: address });

        trackExpertLinkShared({ expertId, channel: 'web_share', target: 'intake_email' });

        return;
      }

      await copyToClipboard(address, () => onCopied('intake_email'), 'Your email copied');

      return;
    }

    if (await shareIntakeEmail(address)) {
      trackExpertLinkShared({ expertId, channel: 'native_share', target: 'intake_email' });
    }
  };

  const shareTargets: ShareTarget[] = [
    ...(url && !isWeb
      ? [{ key: 'qr_image' as const, label: 'QR code', spokenLabel: 'Q R code' }]
      : []),
    ...(url ? [{ key: 'link' as const, label: 'Link' }] : []),
    ...(intakeEmail ? [{ key: 'intake_email' as const, label: 'Email address' }] : []),
  ];

  const runShare = async (target: ShareTarget) => {
    if (!userId) {
      return;
    }

    const expertId = Number(userId);

    try {
      /**
       * The sheet lists what was available when it opened, so a refetch that
       * drops a value between opening and tapping leaves a row pointing at
       * nothing. Saying so beats a tap that appears not to register.
       */
      if (target.key === 'qr_image' && url) {
        await shareQrCode(expertId, url);
      } else if (target.key === 'link' && url) {
        await shareLink(expertId, url);
      } else if (target.key === 'intake_email' && intakeEmail) {
        await shareEmail(expertId, intakeEmail);
      } else {
        toast.showError({ title: 'Unable to share' });
      }
    } catch (error) {
      if (isShareCancellation(error)) {
        return;
      }

      console.error('AccountQrCode:share:error', error);

      toast.showError({ title: 'Unable to share' });
    }
  };

  const onShare = () => {
    if (shareTargets.length === 0) {
      return;
    }

    /** With one thing to share there is nothing to choose between. */
    if (shareTargets.length === 1) {
      runShare(shareTargets[0]).catch(() => undefined);

      return;
    }

    setIsShareSheetOpen(true);
  };

  const onSelectShareTarget = (target: ShareTarget) => {
    setIsShareSheetOpen(false);

    runShare(target).catch(() => undefined);
  };

  if (isLoading) {
    return (
      <QrCodeScreen
        name={name}
        avatarUrl={avatarUrl}
        bottom={bottom}
        background={colors.background[0]}>
        <VStack className="items-center gap-4">
          <Card
            size="md"
            variant="filled"
            className="items-center border border-outline-500 bg-background-0 p-6">
            <Skeleton variant="rounded" className="h-[220px] w-[220px]" />
          </Card>

          <Spinner size="small" />
        </VStack>
      </QrCodeScreen>
    );
  }

  if (isError || (!url && !intakeEmail && !smsNumber)) {
    return (
      <QrCodeScreen
        name={name}
        avatarUrl={avatarUrl}
        bottom={bottom}
        background={colors.background[0]}>
        <Text className="text-center text-typography-700">
          Your sharing details aren't available right now.
        </Text>
      </QrCodeScreen>
    );
  }

  return (
    <QrCodeScreen
      name={name}
      avatarUrl={avatarUrl}
      bottom={bottom}
      background={colors.background[0]}>
      {url ? (
        <>
          <Card
            size="md"
            variant="filled"
            className="items-center border border-outline-500 bg-background-0 p-6">
            {/* Fixed black-on-white regardless of theme: a scanner reading an
                inverted or tinted code is not guaranteed the way a themed UI is. */}
            <QRCode
              value={url}
              size={QR_CODE_SIZE}
              color={colors.black}
              backgroundColor={colors.white}
              getRef={(ref) => {
                qrCodeRef.current = ref;
              }}
            />
          </Card>

          <CopyField
            label="Your link"
            value={url}
            displayValue={displayUrl(url)}
            hint="Anyone who opens this, or scans the code above, can ask you a question."
            onCopied={() => onCopied('link')}
          />
        </>
      ) : null}

      {intakeEmail ? (
        <CopyField
          label="Your email"
          value={intakeEmail}
          hint="Forward a colleague's question here and it becomes an inquiry."
          onCopied={() => onCopied('intake_email')}
        />
      ) : null}

      {smsNumber ? (
        <CopyField
          label="Your text number"
          value={`Text me a question at ${smsNumber}. ${SMS_OPT_IN_NOTICE}`}
          displayValue={formatSmsNumber(smsNumber)}
          hint={SMS_OPT_IN_NOTICE}
          onCopied={() => onCopied('sms_number')}
        />
      ) : null}

      {shareTargets.length > 0 ? (
        <Button size="md" className="w-full" onPress={onShare}>
          <ButtonText>Share</ButtonText>
        </Button>
      ) : null}

      <ShareTargetSheet
        isOpen={isShareSheetOpen}
        onClose={() => setIsShareSheetOpen(false)}
        targets={shareTargets}
        onSelect={onSelectShareTarget}
      />
    </QrCodeScreen>
  );
}

/** The chrome every state of this screen shares: header, identity, one column. */
function QrCodeScreen({
  name,
  avatarUrl,
  bottom,
  background,
  children,
}: {
  name: string;
  avatarUrl: ReturnType<typeof getAvatarUrl>;
  bottom: number;
  background: string;
  children: React.ReactNode;
}) {
  return (
    <VStack className="flex-1">
      <Header
        contentScrollsUnderHeader
        title="Ask me on Primum"
        statusBarBackgroundColor={background}
        backRoute={ROUTES.ACCOUNT}
        isBackButton
      />

      {/* The column scrolls rather than filling the screen. A code, two copy
          rows and the share control are taller than a short laptop viewport,
          and a non-scrolling flex column lets react-native-web shrink its
          children to fit instead, which collapses a copy row onto the next
          one's label. */}
      <ScrollView
        contentInsetAdjustmentBehavior="automatic"
        className="flex-1"
        showsVerticalScrollIndicator={false}
        contentContainerClassName="items-center px-6 py-8"
        contentContainerStyle={{ paddingBottom: Math.max(bottom, 24) }}>
        <VStack className="mx-auto w-full max-w-sm items-center gap-6">
          <HStack className="items-center gap-3">
            <Avatar size="md">
              <AvatarImage source={avatarUrl || primum_default_avatar} />
            </Avatar>

            <Heading size="h3-lg">{name}</Heading>
          </HStack>

          {children}
        </VStack>
      </ScrollView>
    </VStack>
  );
}
